import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { industries } from './data';
import type { IndustryData } from './IndustryPage';
import SEO, { breadcrumbJsonLd } from '../../components/SEO';

/* Group industries: health-adjacent, commerce & distribution, logistics & field */
const FOOD_HEALTH: string[] = ['restaurants', 'pharmacies', 'grocery', 'medical-supplies'];
const RETAIL_DIST: string[] = ['retail', 'ecommerce', 'automotive-parts', 'b2b-distribution'];
const LOGISTICS_FIELD: string[] = [
  'couriers',
  'contract-logistics',
  'manufacturing',
  'field-service',
  'building-supplies',
  'laundry',
  'flowers',
  'water',
  'fuel',
];

const GROUPS: { keys: string[]; title: string; subtitle: string; icon: string; accent: 'blue' | 'emerald' | 'violet' }[] = [
  { keys: FOOD_HEALTH, title: 'Food, health & grocery', subtitle: 'Restaurants, pharmacies, supermarkets, and medical logistics', icon: 'ri-restaurant-line', accent: 'blue' },
  { keys: RETAIL_DIST, title: 'Retail, e‑commerce & distribution', subtitle: 'Store, online, parts, and wholesale delivery', icon: 'ri-store-2-line', accent: 'emerald' },
  { keys: LOGISTICS_FIELD, title: 'Logistics & field operations', subtitle: '3PL, couriers, field service, bulky goods, and specialty', icon: 'ri-truck-line', accent: 'violet' },
];

const ACCENT_CLASSES = {
  blue: { bg: 'bg-blue-100', icon: 'text-blue-600', badge: 'bg-blue-50 text-blue-700', link: 'text-blue-600' },
  emerald: { bg: 'bg-emerald-100', icon: 'text-emerald-600', badge: 'bg-emerald-50 text-emerald-700', link: 'text-emerald-600' },
  violet: { bg: 'bg-violet-100', icon: 'text-violet-600', badge: 'bg-violet-50 text-violet-700', link: 'text-violet-600' },
};

const HERO_STATS = [
  { value: '17+', label: 'Industries' },
  { value: '99%', label: 'On-time delivery' },
  { value: '30%', label: 'Typical cost reduction' },
];

export default function IndustriesIndex() {
  const { t } = useTranslation();

  return (
    <div className="pt-24">
      <SEO
        title="Industry Solutions"
        description="Tailored AI-powered delivery for grocery, B2B distribution, medical & labs, field service, 3PL, automotive parts, building supplies, and more."
        jsonLd={breadcrumbJsonLd([
          { name: 'Home', url: 'https://geofleet.ai' },
          { name: 'Industries', url: 'https://geofleet.ai/industries' },
        ])}
      />
      {/* Hero with dual blurs and stats */}
      <section className="relative py-16 lg:py-20 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
        <div className="absolute top-0 right-0 w-[500px] h-[300px] bg-blue-500/[0.06] rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[250px] bg-emerald-500/[0.05] rounded-full blur-3xl -translate-x-1/2" />
        <div className="absolute top-1/2 left-1/2 w-[300px] h-[200px] bg-violet-500/[0.04] rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="ri-building-2-line" />
            <span>{t('industries.badge')}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-gray-900 tracking-tight mb-4">
            {t('industries.title')}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-10">
            {t('industries.subtitle')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12">
            {HERO_STATS.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-sm text-gray-500 mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Grouped industry cards */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          {GROUPS.map((group) => {
            const list = group.keys.map((slug) => industries[slug]).filter(Boolean) as IndustryData[];
            if (list.length === 0) return null;
            const acc = ACCENT_CLASSES[group.accent];
            return (
              <div key={group.title} className="mb-14 last:mb-0">
                <div className="flex items-center gap-2 mb-6">
                  <div className={`w-10 h-10 rounded-xl ${acc.bg} flex items-center justify-center`}>
                    <i className={`${group.icon} ${acc.icon} text-xl`} />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">{group.title}</h2>
                </div>
                <p className="text-sm text-gray-500 mb-6 max-w-2xl">{group.subtitle}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6">
                  {list.map((industry) => (
                    <IndustryCard key={industry.slug} industry={industry} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA strip — primary + secondary */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('industries.ctaTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('industries.ctaSubtitle')}</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://app.geofleet.ai/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25"
            >
              {t('common.ctaStartTrial')}
              <i className="ri-arrow-right-line text-sm" />
            </a>
            <a
              href="https://meetings.hubspot.com/aditya145"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-emerald-700 border-2 border-emerald-500 hover:bg-emerald-50 transition-all"
            >
              <i className="ri-calendar-line text-emerald-600" />
              {t('industries.ctaButton')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function IndustryCard({ industry }: { industry: IndustryData }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/industries/${industry.slug}`}
      className="group block bg-white rounded-2xl border border-gray-100 p-6 lg:p-7 hover:border-gray-200 hover:shadow-xl hover:shadow-gray-200/50 transition-all duration-300"
    >
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-105 transition-transform duration-300"
        style={{ backgroundColor: industry.colorLight }}
      >
        <i className={`${industry.icon} text-2xl`} style={{ color: industry.color }} />
      </div>
      {industry.stats && industry.stats.length > 0 && (
        <div className="flex gap-3 mb-3">
          {industry.stats.slice(0, 3).map((s) => (
            <span key={s.label} className="text-xs font-semibold text-gray-500">
              {s.value} <span className="font-normal text-gray-400">{s.label}</span>
            </span>
          ))}
        </div>
      )}
      <h2 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
        {industry.name}
      </h2>
      <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-4">
        {industry.description}
      </p>
      <span
        className="inline-flex items-center gap-1.5 text-sm font-semibold"
        style={{ color: industry.color }}
      >
        <span>{t('industries.learnMore')}</span>
        <i className="ri-arrow-right-line text-base group-hover:translate-x-0.5 transition-transform" />
      </span>
    </Link>
  );
}
